import React, { ReactNode, createContext, useMemo } from 'react'

// Says on whose behalf a role's work fields are being written (REU04-D14).
//
// A meeting step wraps its role blocks in this provider to declare "these edits
// are made from meeting X, by someone taking part in it".
//
// Only useSaveRoleField reads it (through getRoleFieldWrite): it opens
// checklist, indicators and notes for editing, even on a role the reader does
// not hold, and hands the meetingId to updateRole with the save, so the server
// can re-check. useUpdateRole must not read it: it lives at the org level,
// above every meeting step, and always saw nothing (#170).
//
// 🔴 The provider grants nothing on its own. The backend verifies all four
// conditions itself (meeting still running, caller in the room, role inside the
// meeting's perimeter, work fields only) — see backend meetingWriteScope.ts.
// This context only decides what the screen offers; the server decides what
// happens. Offering a field the server would refuse is exactly the failure of
// 08/08/2026, which is why the two rules are written to say the same thing.
//
// Absent this provider — the whole product outside these two steps — the hooks
// behave exactly as they did before REU-04.

export interface RoleWriteScope {
  meetingId: string
  // Whether the reader may write from this meeting at all. False for a guest
  // watching a meeting read-only: the fields stay closed rather than being
  // offered and then refused.
  canWrite: boolean
}

export const RoleWriteScopeContext = createContext<RoleWriteScope | undefined>(
  undefined
)

interface Props extends RoleWriteScope {
  children: ReactNode
}

export function RoleWriteScopeProvider({
  meetingId,
  canWrite,
  children,
}: Props) {
  const value = useMemo(
    () => ({ meetingId, canWrite }),
    [meetingId, canWrite]
  )
  return (
    <RoleWriteScopeContext.Provider value={value}>
      {children}
    </RoleWriteScopeContext.Provider>
  )
}
